import React, { useEffect, useState } from 'react';
import { Form, Checkbox } from 'antd';
import {
  FormInput,
  IconDoer,
  IconSmallArrow,
  OrangeButton,
  TooltipIcon,
  SelectOption,
} from 'components';
import classNames from 'classnames';
import { createPartner } from 'api/api';
import { useNavigate } from 'react-router-dom';
import { COLORS } from 'utils/colors';
import { ROLE_TYPES, SLH_TYPES } from 'api/constants';
import { useUser } from 'context/userContext';
import { useAuth } from 'context/authContext';
import { allowOnlyDigits } from 'utils/helpers';

export const CreateChildPartner = () => {
  const { userDetails } = useUser();
  const { parentId } = useAuth();
  const navigate = useNavigate();
  const [isBtnDisabled, setIsBtnDisabled] = useState(true);
  const [totalBeds, setTotalBeds] = useState(1);
  const [formData, setFormData] = useState({
    slh: {
      name: '',
      region: '',
      rank: 1,
      gender: '',
      doer: false,
      parentId: parentId,
      bed_count: totalBeds,
      address: '',
      website: '',
      weekly_rate: null,
      type: SLH_TYPES.child,
      fiscal_start_dow: 'Sunday',
    },
    manager: undefined,
  });

  const onValuesChange = (changedFields) => {
    const prefixes = ['slh_', 'manager_'];

    for (const key in changedFields) {
      for (let prefix of prefixes) {
        if (key.startsWith(prefix)) {
          prefix = prefix.slice(0, -1);
          const propKey = key.slice(prefix.length + 1);
          setFormData({
            ...formData,
            [prefix]: { ...formData[prefix], [propKey]: changedFields[key] },
          });
        }
      }
    }
  };

  useEffect(() => {
    const isFormValid =
      !!formData.slh.name &&
      !!formData.slh.bed_count &&
      !!formData.slh.weekly_rate &&
      !!formData.slh.gender;
    setIsBtnDisabled(!isFormValid);
  }, [formData]);

  const handleGenderChange = (genders) => {
    const selectedGenders = genders.join(',');

    setFormData({
      ...formData,
      slh: {
        ...formData.slh,
        gender: selectedGenders,
      },
    });
  };

  const handleDoerChange = (e) => {
    setFormData({
      ...formData,
      slh: {
        ...formData.slh,
        doer: e.target.checked,
      },
    });
  };

  const handleFiscalWeekStartDayChange = (e) => {
    const selectedFiscalWeekStartDay = e;

    setFormData({
      ...formData,
      slh: {
        ...formData.slh,
        fiscal_start_dow: selectedFiscalWeekStartDay,
      },
    });
  };

  const handleFormSubmit = () => {
    let formDataToSubmit = {};

    if (formData.manager) {
      formDataToSubmit = {
        ...formData,
        slh: {
          ...formData.slh,
          bed_count: +totalBeds,
        },
        manager: { ...formData.manager, country_code: '+1' },
      };
    } else {
      formDataToSubmit = {
        ...formData,
        slh: {
          ...formData.slh,
          bed_count: +totalBeds,
        },
      };
    }

    createPartner(formDataToSubmit, ROLE_TYPES.slh_manager, () => {
      navigate('/slh-children');
    });
  };

  const sanitizeDigitsOnInput = (e) => {
    const input = e.target;
    const rawValue = input.value;
    const digitsOnly = rawValue.replace(/\D/g, '');

    const prefixDigits = (formData.manager?.country_code ?? '+1').replace(/\D/g, '');
    const sanitized = digitsOnly.startsWith(prefixDigits)
      ? digitsOnly.slice(prefixDigits.length)
      : digitsOnly;

    if (rawValue !== sanitized) {
      input.value = sanitized;
    }
  };

  return (
    <Form
      name="create-client-by-admin"
      className="flex flex-col w-full gap-6"
      onValuesChange={onValuesChange}
      requiredMark={false}
    >
      <div className="flex justify-between w-full">
        <div className="flex items-center gap-x-4">
          <div
            onClick={() => navigate(-1)}
            className="w-10 h-10 bg-white rounded-lg shadow-block-shadow flex items-center justify-center cursor-pointer"
          >
            <IconSmallArrow className="w-2 h-4" />
          </div>
          <p className="nunito-title">Add new partner (slh)</p>
        </div>
        <OrangeButton onClick={handleFormSubmit} label="Save" disabled={isBtnDisabled} />
      </div>
      <div className="flex flex-wrap items-start gap-6">
        <div className="flex flex-col w-full base-block pt-[30px]">
          <p className="nunito-title px-[30px] pb-[11px]">SLH</p>
          <div
            className={classNames(
              'flex flex-wrap relative border-t border-color-lightBlue p-[30px] pt-3 gap-x-8 gap-y-6',
              'before:content-[""] before:absolute before:w-4 before:h-4 before:rounded-full before:border before:border-color-divider before:bg-white before:z-10 before:top-0 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2',
            )}
          >
            <FormInput
              label="Name*"
              name="slh_name"
              placeholder="Enter Name"
              required
              errorMessage="Please enter valid name"
              className="w-[calc(50%-16px)]"
            />
            <FormInput
              label="Region"
              name="slh_region"
              placeholder="Enter region"
              className="w-[calc(50%-16px)]"
            />
            <div className="flex  w-[calc(50%-16px)]">
              <div className="flex flex-col gap-y-2">
                <p className="text-14 font-medium">Beds Count*</p>
                <div className="flex items-center gap-x-1">
                  <button
                    type="button"
                    onClick={() => setTotalBeds(+totalBeds - 1)}
                    disabled={totalBeds <= 1}
                    className={`w-10 h-10 shadow-block-shadow text-center rounded-ten ${totalBeds <= 1 ? 'opacity-50' : ''}`}
                  >
                    -
                  </button>
                  <input
                    onChange={(e) => setTotalBeds(Math.max(1, +e.target.value || 1))}
                    type="number"
                    min={1}
                    className="w-10 h-10 border border-divider outline-divider outline-1 text-center rounded-ten text-14"
                    value={totalBeds}
                  />
                  <button
                    type="button"
                    onClick={() => setTotalBeds(+totalBeds + 1)}
                    className="w-10 h-10 shadow-block-shadow text-center rounded-ten"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
            <FormInput
              label="Address"
              name="slh_address"
              placeholder="Enter address"
              className="w-[calc(50%-16px)]"
            />
            <div className="flex flex-col justify-between gap-y-6 w-[calc(50%-16px)]">
              <div className="flex flex-col gap-y-2 w-full">
                <p className="text-14 font-medium">Gender*</p>
                <Checkbox.Group
                  onChange={handleGenderChange}
                  className="w-full gap-x-4"
                  name="gender"
                  required
                >
                  <Checkbox
                    className="px-4 py-2.5 bg-white border font-medium rounded-xl gap-x-2"
                    value={'M'}
                  >
                    Male
                  </Checkbox>
                  <Checkbox
                    className="px-4 py-2.5 bg-white border font-medium rounded-xl gap-x-2"
                    value={'F'}
                  >
                    Female
                  </Checkbox>
                  <Checkbox
                    className="px-4 py-2.5 bg-white border font-medium rounded-xl gap-x-2"
                    value={'O'}
                  >
                    Other
                  </Checkbox>
                </Checkbox.Group>
              </div>

              <div className="flex flex-col gap-y-2 w-full">
                <p className="text-14 font-medium">D.O.E.R. Program*</p>
                <div className="flex items-center gap-x-3">
                  <IconDoer className="w-8 h-8" />
                  <Checkbox
                    className="px-4 py-2.5 bg-white border font-medium rounded-xl gap-x-2"
                    checked={formData.slh.doer}
                    onChange={handleDoerChange}
                  >
                    D.O.E.R. Support
                  </Checkbox>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-y-6 w-[calc(50%-16px)]">
              <div className="flex flex-row justify-between gap-x-6">
                <FormInput
                  label="Website"
                  name="slh_website"
                  placeholder="Enter website"
                  className="w-[calc(50%-16px)]"
                />
                <FormInput
                  label="Weekly Rate*"
                  name="slh_weekly_rate"
                  type="number"
                  placeholder="$"
                  required
                  className="w-[calc(50%-16px)]"
                  onKeyDown={allowOnlyDigits}
                  onWheel={(e) => {
                    e.target.blur();
                    e.stopPropagation();

                    setTimeout(() => {
                      e.target.focus();
                    }, 0);
                  }}
                />
              </div>
              <div className="flex flex-col gap-x-6 gap-y-2 flex-1">
                <div className="flex justify-flex-start items-center">
                  <p className="text-14 font-medium">Fiscal Week Start Day</p>
                  <TooltipIcon tooltipText="The type is permanent and can not be modified later" />
                </div>
                <SelectOption
                  dropdownStyle={{ padding: '16px', background: 'white' }}
                  defaultValue={{ value: 'Sunday' }}
                  onChange={handleFiscalWeekStartDayChange}
                  suffixIcon={
                    <IconSmallArrow
                      color={COLORS.black}
                      className={`rotate-[-90deg] w-3 h-3 opacity-40`}
                    />
                  }
                  className="h-[44px] text-base font-medium base-block rounded-lg"
                  dropdownRender={(originalNode) => originalNode}
                  optionRender={(option) => (
                    <div
                      className={`flex items-center px-2 py-1 gap-x-1 text-black rounded-md capitalize border border-color-divider bg-white overflow-hidden ${formData.slh.fiscal_start_dow === option.value ? 'border-orange' : ''}`}
                    >
                      <span
                        className={`w-6 h-6  rounded-full border border-orange ${formData.slh.fiscal_start_dow === option.value ? 'border-[6px]' : ''}`}
                      />
                      <p className="font-14">{option.value}</p>
                    </div>
                  )}
                  options={[
                    { value: 'Sunday' },
                    { value: 'Monday' },
                    { value: 'Tuesday' },
                    { value: 'Wednesday' },
                    { value: 'Thursday' },
                    { value: 'Friday' },
                    { value: 'Saturday' },
                  ]}
                />
              </div>
            </div>
            <div className="flex gap-x-6 gap-y-2 w-[calc(50%-16px)]">
              <div className="flex flex-col gap-x-6 gap-y-2 min-w-36 w-full">
                <div className="flex justify-flex-start items-center">
                  <p className="text-14 font-medium">Type</p>
                  <TooltipIcon tooltipText="The type is permanent and can not be modified later" />
                </div>
                <SelectOption
                  dropdownStyle={{ padding: '16px', background: 'white' }}
                  disabled
                  defaultValue={{ label: 'Child', value: '3' }}
                  suffixIcon={
                    <IconSmallArrow
                      color={COLORS.black}
                      className={`rotate-[-90deg] w-3 h-3 opacity-40`}
                    />
                  }
                  className="h-[44px] text-base font-medium base-block rounded-lg"
                  placeholder="Choose Type"
                  dropdownRender={(originalNode) => originalNode}
                  optionRender={(option) => (
                    <div className="flex items-center px-2 py-1 gap-x-1 text-black rounded-md capitalize duration-150 hover:bg-orange">
                      {/* <IconSoberLiving /> */}
                      <p className="font-14">{option.label}</p>
                    </div>
                  )}
                  options={[{ label: 'Child', value: '3' }]}
                />
              </div>
            </div>
            <div className="flex flex-col gap-x-6 gap-y-2 w-[calc(50%-16px)]">
              <div className="flex justify-flex-start items-center flex-1">
                <p className="text-14 font-medium">Parent</p>
                <TooltipIcon tooltipText="The type is permanent and can not be modified later" />
              </div>
              <SelectOption
                dropdownStyle={{ padding: '16px', background: 'white' }}
                value={
                  userDetails?.partner_details?.parent
                    ? userDetails?.partner_details?.parent.name
                    : userDetails?.partner_details?.name
                }
                required
                disabled
                suffixIcon={
                  <IconSmallArrow
                    color={COLORS.black}
                    className={`rotate-[-90deg] w-3 h-3 opacity-40`}
                  />
                }
                className="h-[44px] text-base font-medium base-block rounded-lg capitalize"
                placeholder="Select Parent"
                dropdownRender={(originalNode) => originalNode}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col w-full base-block pt-[30px]">
          <p className="nunito-title px-[30px] pb-[11px] uppercase">SLH Manager</p>
          <div
            className={classNames(
              'flex flex-wrap relative border-t border-color-lightBlue p-[30px] pt-3 gap-x-8 gap-y-6',
              'before:content-[""] before:absolute before:w-4 before:h-4 before:rounded-full before:border before:border-color-divider before:bg-white before:z-10 before:top-0 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2',
            )}
          >
            <FormInput
              label="Name"
              name="manager_firstname"
              placeholder="Enter manager's name"
              className="w-[calc(50%-16px)]"
              errorMessage="Please enter valid name"
            />
            <FormInput
              label="Last Name"
              name="manager_lastname"
              placeholder="Enter manager's lastname"
              className="w-[calc(50%-16px)]"
              errorMessage="Please enter valid lastname"
            />
            <FormInput
              className="w-[calc(50%-16px)]"
              label="Phone"
              type="text"
              pattern="['0-9']*"
              maxLength={10}
              name="manager_phone"
              placeholder="Phone"
              prefix="+1"
              onKeyDown={allowOnlyDigits}
              onWheel={(e) => {
                e.target.blur();
                e.stopPropagation();

                setTimeout(() => {
                  e.target.focus();
                }, 0);
              }}
              onInput={sanitizeDigitsOnInput}
            />
            <FormInput
              type="email"
              className="w-[calc(50%-16px)]"
              label={
                <>
                  <p className="text-14 font-medium">Email</p>
                  <TooltipIcon tooltipText="This email will be used as SLH Managed Login credential" />
                </>
              }
              name="manager_email"
              placeholder="Email"
              errorMessage="Please enter valid email"
            />
          </div>
        </div>
      </div>
    </Form>
  );
};
